import { DropdownItem } from '../models';

interface ISequenceMarkValues {
  good: string;
  bad: string;
}

export const simulatorTypes = Object.freeze({
  STANDARD_SIMULATOR: 'simulator',
  LITE_SIM: 'real_mri',
} as const);

export const sequenceMarkValues: ISequenceMarkValues = {
  good: '1',
  bad: '2',
};

export const sequenceStatusValues = Object.freeze({
  completed: 'finished',
  started: 'started',
  none: '',
  runnable: 'runnable',
  running: 'running',
  kernel: 'kernel',
  error: 'error',
  cancelled: 'cancelled',
  cancelledError: 'cancelled-error',
  confirm: 'confirm',
  confirmed: 'confirmed',
} as const);

export const sequenceSaveStatus = Object.freeze({
  SAVED: 'SAVED',
  UNSAVED: 'UNSAVED',
} as const);

export const viewerDropdownActions = {
  set_in_plane_pos: 'set-in-plane-position',
  set_in_perpendicular_pos: 'set-in-perpendicular-position',
  copy_image_pos: 'copy-image-position',
  toggle_multi_slices: 'toggle-multi-slices',
  toggle_multi_intersections: 'toggle-multi-intersections',
  toggle_intersections_only: 'toggle-intersections-only',
  switch_fe_pe: 'switch-fe-pe',
  toggle_auto_play: 'toggle-auto-play',
  set_sat_band_in_horizontal_pos: 'set-sat-band-in-horizontal-position',
  set_sat_band_in_vertical_pos: 'set-sat-band-in-vertical-position',
};

export const anatomicalRegionConstants = {
  spine: 'Spine',
  abdomen: 'Abdomen',
  pelvis: 'Pelvis',
  left_shoulder_and_elbow: 'Left shoulder and elbow',
  right_foot: 'Right foot',
  left_hand: 'Left hand',
  right_knee: 'Right knee',
};

export const isocenterTypeConstants = {
  whole_body: 'whole-body',
  regional: 'region',
};

export const viewerDropdownItems: DropdownItem[] = [
  {
    id: '1',
    text: 'In-plane position',
    action: viewerDropdownActions.set_in_plane_pos,
  },
  {
    id: '2',
    text: 'Perpendicular position',
    action: viewerDropdownActions.set_in_perpendicular_pos,
  },
  {
    id: '3',
    text: 'Copy image position',
    action: viewerDropdownActions.copy_image_pos,
  },
  {
    id: '4',
    text: 'Switch FE/PE',
    action: viewerDropdownActions.switch_fe_pe,
  },
  {
    id: '5',
    text: 'Toggle multi-slices',
    action: viewerDropdownActions.toggle_multi_slices,
    checked: false,
  },
  {
    id: '6',
    text: 'Toggle multi-intersections',
    action: viewerDropdownActions.toggle_multi_intersections,
    checked: false,
  },
  {
    id: '7',
    text: 'Toggle intersections only',
    action: viewerDropdownActions.toggle_intersections_only,
    checked: false,
  },
  {
    id: '8',
    text: 'Start/stop auto-play',
    action: viewerDropdownActions.toggle_auto_play,
  },
  {
    id: '9',
    text: 'Position sat. band horizontally',
    action: viewerDropdownActions.set_sat_band_in_horizontal_pos,
  },
  {
    id: '10',
    text: 'Position sat. band in vertically',
    action: viewerDropdownActions.set_sat_band_in_vertical_pos,
  },
];

export const viewerDropdownItemsIds = Object.freeze({
  IN_PLANE_POSITION: '1',
  PERPENDICULAR_POSITION: '2',
  COPY_IMAGE_POSITION: '3',
  SWITCH_FE_PE: '4',
  TOGGLE_MULTI_SLICES: '5',
  TOGGLE_MULTI_INTERSECTIONS: '6',
  TOGGLE_INTERSECTIONS_ONLY: '7',
  START_STOP_AUTO_PLAY: '8',
  POSITION_SAT_BAND_HORIZONTALLY: '9',
  POSITION_SAT_BAND_VERTICALLY: '10',
} as const);

export const planeOrientationConstants = {
  rl: 'RL',
  ap: 'AP',
  fh: 'FH',
};

export const renderAreas = {
  leftViewer: 'viewer-1',
  centerViewer: 'viewer-2',
  rightViewer: 'viewer-3',
};

export const commonConstants = Object.freeze({
  YES: 'yes',
  NO: 'no',
  NONE: 'none',
  NA: 'n/a',
  N: 'n',
} as const);

export const spatialTypes = Object.freeze({
  SLICES_GROUP: 'slicesGroup',
  SATURATION_BAND: 'saturationBand',
} as const);

export const pulseSequenceSchemeIds = Object.freeze({
  GRE: 1,
  MOLLI_OLD: 2,
  BSSFP: 3,
  SE: 4,
  TSE: 5,
  IR_BSSFP: 6,
  IR_SE: 7,
  EPI: 8,
  SE_EPI: 9,
  GRE_3D: 10,
  IR_TSE: 11,
  SS_FSE: 12,
  GRE_OOP: 13,
  GRE_CONC: 14,
  MP_RAGE: 15,
  MOLLI: 16,
  PG_SE_EPI: 17,
  DIXON: 18,
  SWI_2D_GRE: 19,
  CINE_BSSFP: 20,
  PERFUSION_GRE: 21,
  FLAIR: 22,
  TRACE_DWI: 23,
  TSE_3D: 24,
  TI_SCOUT: 25,
  T2_MAP: 26,
  LGE: 27,
} as const);

export const TOFSequencesFileNames = ['GRE-2D-TOF', 'gre-3d-tof'];

export const copyOptions = Object.freeze({
  ALL: 'ALL',
  SLICE_NUMBER: 'SLICE_NUMBER',
  CENTER: 'CENTER',
} as const);

export const teValuesConstants = Object.freeze({
  EFFECTIVE_TE: 'Effective TE',
  ECHO_SPACING: 'Echo spacing (ESP)',
} as const);

const constants = {
  sequenceMarkValues,
  sequenceStatusValues,
};

export const REAL_MRI_ANATOMICAL_MODEL = 100001;

export default constants;
